import { useEffect, useMemo, useRef, useState, type KeyboardEvent, type ReactNode } from "react";
import { useT } from "../i18n";
import { Marke } from "./index";
import { KundeAnlegen } from "./KundeAnlegen";
import { Blattvonunten } from "./Blattvonunten";
import { useKunden } from "../daten/haken";
import { useTelefon } from "../schale/geraet";
import type { Kunde } from "../daten/typen";
import type { KundenArt } from "./KundeFormular";
import { kuerzelAus } from "./kuerzel";
import { SperrSatz } from "./Kundensperre";
import "../stile/kundenwahl.css";

/* ══════════════════════════════════════════════════════════════════
   Die Kundenauswahl — ein Aufklapper, kein nacktes Textfeld.

   Bis zum 15.09.2026 war der Kunde ein Eingabefeld mit einer Liste
   darunter, und gewählt war, wessen Name buchstabengenau im Feld stand.
   Das hatte zwei Folgen: Ein Tippfehler löste die Wahl still auf, und
   „Kunde anlegen" führte aus der Maske hinaus.

   Jetzt ist der Kunde ein **Wert**, nicht ein Wort. Zu ist er eine ruhige
   Zeile — Zeichen, Name, Ort, Nummer —, die man anklickt, um zu wechseln.
   Offen ist er eine Liste mit Suchfeld oben, Haken am Gewählten, und
   **ganz unten „Kunde anlegen"**, was das Deckblatt öffnet und den neuen
   Kunden danach hier hineinstellt. Die Maske darunter merkt davon nichts
   außer dem Wert, der sich ändert.

   Gesucht wird über Name, Ort und Nummer — am Telefon sagt der eine
   „Hempen", der andere „die aus Garrel", der dritte liest die Nummer vom
   Lieferschein ab. Der Stamm kommt in einem Abruf; solange er in einen
   passt, ist Filtern hier billiger als eine Anfrage je Anschlag.

   Tastatur: Pfeile bewegen, Enter wählt, Esc schließt, Tab geht weiter.
   Wer nur die Tastatur hat, kommt ohne Maus hindurch.

   ── Am Telefon ein Blatt von unten (18.09.2026) ───────────────────

   Luca, beim Durchklicken der Handy-Ansicht: *„Das Bottom Sheet, das du
   bei der Kundenwahl öffnest, auch kleiner machen, sodass man es besser
   erreichen kann und dass man entsprechend nur wenige Kunden zum Scrollen
   anzeigt. Sieht einfach ein bisschen cooler aus."*

   Es war bis dahin **kein** Blatt von unten, sondern eine Liste, die unter
   ihrem Anker aufging — gebaut für die Maus. Am Telefon hieß das: Sie
   erschien dort, wo das Feld gerade stand (in der Vermietfläche ganz
   oben), sie war mindestens 300 px breit und rollte in sich, und
   geschlossen wurde sie mit einem Tipper daneben. Nichts davon ist eine
   Geste, die ein Daumen kennt.

   **Am Telefon ist es jetzt derselbe Baustein wie jedes andere Blatt**
   (`Blattvonunten`): von unten herein, Griff zum Herunterziehen, Kreuz in
   der Ecke, `Esc` und die Zurückgeste. Der Rastpunkt `klein` hält es
   niedrig — es zeigt vier bis fünf Kunden, der Rest rollt, und darüber
   bleibt die Maske stehen, in die der Kunde hinein soll.

   **Der Inhalt ist in beiden Fällen derselbe** (`Inhalt` unten): ein
   Suchfeld, die Liste, und unten der Weg zum neuen Kunden. Zwei Fassungen
   desselben Inhalts liefen sonst auseinander — die Liste hatte am Telefon
   schon einmal ihre eigene Zeilenform.

   **Zwei Dinge gelten am Telefon nicht.** Das Suchfeld bekommt **keinen
   Fokus** beim Öffnen — sonst steht die Tastatur über dem Blatt, bevor
   jemand es gesehen hat (Luca, 18.09.2026, über die Datumswahl: *„auf
   jeden Fall nicht die Tastatur triggern, weil das einen verrückt
   macht"*); der Regelweg ist die Liste, das Feld die Abkürzung. Und der
   Tipper daneben schließt nicht — das tut der Grund des Blattes, und der
   liegt im Portal und nicht in `raum`.
   ══════════════════════════════════════════════════════════════════ */

/** Zwei Buchstaben aus dem Namen — „Hempen Agrar" wird „HA". */
function zeichenAus(name: string): string {
  // K-02: über Zeichen, nicht über `charAt` — sonst zerfällt ein Emoji.
  return kuerzelAus(name);
}

export function Kundenwahl({
  id, wert, aendern, art = "kunde", anlegen: mitAnlegen = true, alle, anker,
}: {
  /** Für das `<label htmlFor>` der Maske. */
  id: string;
  wert: Kunde | null;
  aendern: (kunde: Kunde | null) => void;
  /**
   * **Ein eigener Anker statt der Zeile mit Zeichen und „Ändern"** (20.09.2026).
   *
   * Am Telefon steht der Kunde im Rahmen des Vermietens und im Abschluss der
   * Sammlung als **Zeile der Handyfassung** (`Zweizeiler`) neben Einsatzort
   * und Zeitraum — drei gleiche Zeilen in einem Blatt, wie die Eckdaten im
   * Angebot. Der Anker hier war die eine Zeile, die anders aussah. Was sich
   * öffnet, bleibt dasselbe: das Blatt mit Suche, Liste und „Kunde anlegen".
   * Ein zweiter Kundendialog wäre der teurere Weg.
   */
  anker?: (oeffnen: () => void, offen: boolean) => ReactNode;
  /** Womit „Kunde anlegen" aufgeht. Die Schnellerfassung braucht einen
   *  Kunden; das Angebot darf mit einem Interessenten beginnen. */
  art?: KundenArt;
  /**
   * **Als Filter statt als Eingabe** (18.09.2026, Belegsuche). Wer nach
   * einem Kunden *sucht*, legt keinen an — `anlegen={false}` nimmt den Weg
   * unten weg. Und ein Filter muss sich zurücknehmen lassen: `alle` ist der
   * i18n-Schlüssel für „Alle Kunden". Er steht im Anker, solange nichts
   * gewählt ist, und unten in der Liste an der Stelle von „Kunde anlegen".
   */
  anlegen?: boolean;
  alle?: string;
}) {
  const t = useT();
  const telefon = useTelefon();
  const raum = useRef<HTMLDivElement>(null);
  const sucheFeld = useRef<HTMLInputElement>(null);
  const [offen, setzeOffen] = useState(false);
  const [wort, setzeWort] = useState("");
  const [aktiv, setzeAktiv] = useState(0);
  const [anlegen, setzeAnlegen] = useState(false);

  const { data, isPending } = useKunden();
  const stamm = useMemo(() => data?.zeilen ?? [], [data]);

  const treffer = useMemo(() => {
    const w = wort.trim().toLowerCase();
    return stamm
      .filter((k) => !w || `${k.name} ${k.ort} ${k.nr}`.toLowerCase().includes(w))
      .slice()
      .sort((a, b) => a.name.localeCompare(b.name, "de"));
  }, [stamm, wort]);

  const oeffnen = () => { setzeWort(""); setzeAktiv(0); setzeOffen(true); };
  const zu = () => setzeOffen(false);

  /* Offen heißt am Schreibtisch: Das Suchfeld hat den Fokus. Wer klickt,
     will tippen. **Am Telefon nicht** — dort stünde die Tastatur über dem
     Blatt, bevor jemand es gesehen hat, und der Regelweg ist die Liste. */
  useEffect(() => { if (offen && !telefon) sucheFeld.current?.focus(); }, [offen, telefon]);

  /* Eine Liste, die offen bleibt, wenn man daneben klickt, ist eine, die
     man wegklicken will und nicht kann. **Am Telefon macht das der Grund
     des Blattes**; hier dürfte es gar nicht laufen, weil das Blatt im
     Portal hängt und damit außerhalb von `raum` liegt — jeder Tipper darin
     zählte als „daneben". */
  useEffect(() => {
    if (!offen || telefon) return;
    const draussen = (e: MouseEvent) => {
      if (!raum.current?.contains(e.target as Node)) setzeOffen(false);
    };
    document.addEventListener("mousedown", draussen);
    return () => document.removeEventListener("mousedown", draussen);
  }, [offen, telefon]);

  // Beim Tippen fängt die Markierung wieder oben an.
  useEffect(() => { setzeAktiv(0); }, [wort]);

  const waehlen = (k: Kunde) => { aendern(k); setzeOffen(false); };

  const zumAnlegen = () => { setzeOffen(false); setzeAnlegen(true); };

  const tasten = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setzeAktiv((a) => Math.min(a + 1, treffer.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setzeAktiv((a) => Math.max(a - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const k = treffer[aktiv];
      if (k) waehlen(k);
      else if (mitAnlegen) zumAnlegen();
    } else if (e.key === "Escape") {
      e.preventDefault();
      e.stopPropagation();
      zu();
    } else if (e.key === "Tab") {
      zu();
    }
  };

  /* Die markierte Zeile bleibt im Bild, auch wenn die Liste rollt. Gesucht
     wird am Dokument und nicht in `raum`: Am Telefon steht die Liste im
     Blatt, und das hängt im Portal. */
  useEffect(() => {
    if (!offen) return;
    document.getElementById(`${id}-o${aktiv}`)?.scrollIntoView({ block: "nearest" });
  }, [aktiv, offen, id]);

  const listeId = `${id}-liste`;
  const suchwort = wort.trim();

  /* ── Der Inhalt, einmal geschrieben ──
     Am Schreibtisch steht er in der Liste unter dem Anker, am Telefon im
     Blatt von unten. Zwei Abschriften desselben Inhalts liefen auseinander,
     sobald an einer davon eine Spalte dazukommt. */
  const suchfeld = (
    <input
      ref={sucheFeld}
      className="kw-suche"
      type="search"
      autoComplete="off"
      role="combobox"
      aria-label={t("kundenwahl.sucheSchild")}
      aria-expanded="true"
      aria-controls={listeId}
      aria-autocomplete="list"
      aria-activedescendant={treffer[aktiv] ? `${id}-o${aktiv}` : undefined}
      placeholder={t("kundenwahl.suchePlatz")}
      value={wort}
      onChange={(e) => setzeWort(e.target.value)}
      onKeyDown={tasten}
    />
  );

  const liste = (
    <ul className="kw-liste" role="listbox" id={listeId} aria-label={t("kundenwahl.liste")}>
      {treffer.map((k, i) => {
        const gewaehlt = wert?.nr === k.nr && wert?.name === k.name;
        return (
          <li
            key={`${k.nr}·${k.name}`}
            id={`${id}-o${i}`}
            role="option"
            aria-selected={gewaehlt}
            className={`kw-zeile${i === aktiv ? " aktiv" : ""}`}
            onPointerEnter={(e) => { if (e.pointerType === "mouse") setzeAktiv(i); }}
            onClick={() => waehlen(k)}
          >
            <span className="kw-zeichen" aria-hidden="true">{zeichenAus(k.name)}</span>
            <span className="kw-text">
              <b>{k.name}</b>
              <span>{k.ort || t("allgemein.strich")}</span>
            </span>
            {k.art === "interessent" ? (
              <span className="kw-marke">{t("kundenwahl.interessent")}</span>
            ) : null}
            {/* AG-04: Ein gesperrter Kunde stand in der Liste wie jeder andere. */}
            {k.gesperrt ? (
              <span className="kw-marke kw-gesperrt" title={k.sperrgrund}>{t("kundenwahl.gesperrt")}</span>
            ) : null}
            <span className="kw-nr">{k.nr}</span>
            {/* Der Haken trägt für das Auge; für den Schirmleser
                sagt es `aria-selected`. */}
            <span className="kw-haken" aria-hidden="true">
              {gewaehlt ? t("kundenwahl.haken") : ""}
            </span>
          </li>
        );
      })}
      {!treffer.length ? (
        <li className="kw-nichts" role="presentation">
          {isPending
            ? t("allgemein.laedt")
            : suchwort
              ? t("kundenwahl.nichtsGefunden", { wort: suchwort })
              : t("kundenwahl.stammLeer")}
        </li>
      ) : null}
    </ul>
  );

  /* Ganz unten, immer: der Weg zum neuen Kunden. Was in der Suche stand,
     geht als Firmenname mit — man tippt ihn nicht zweimal. Als Filter steht
     hier stattdessen der Weg zurück zu allen. */
  const fussknopf = mitAnlegen ? (
    <button type="button" className="kw-neu" onClick={zumAnlegen}>
      {suchwort
        ? t("kundenwahl.anlegenMit", { wort: suchwort })
        : t("kundenwahl.anlegen")}
    </button>
  ) : alle && wert ? (
    <button type="button" className="kw-neu" onClick={() => { aendern(null); zu(); }}>
      {t(alle)}
    </button>
  ) : null;

  /* **Was die Sperre heißt, steht unter dem Gewählten** (AG-04, Durchgang
     03.10.2026): Vera baute ein Angebot an einen gesperrten Kunden, ohne es
     zu merken — die Sperre wirkte erst als Ampel im Abrechnungslauf. Ein
     Angebot bleibt erlaubt; der Satz sagt nur, was danach passiert. */
  const sperrsatz = <SperrSatz kunde={wert} />;

  return (
    <div className="kw" ref={raum}>
      {/* Der Anker: zu eine ruhige Zeile mit dem Gewählten, oder das leere
          Feld. Beides ist ein Knopf — er öffnet die Liste. */}
      {anker ? anker(offen ? zu : oeffnen, offen) : (
      <button
        type="button"
        id={id}
        className={wert ? "kw-anker kw-gewaehlt" : "kw-anker kw-leer"}
        aria-haspopup="listbox"
        aria-expanded={offen}
        aria-controls={offen ? listeId : undefined}
        onClick={offen ? zu : oeffnen}
      >
        {wert ? (
          <>
            <span className="kw-zeichen" aria-hidden="true">{zeichenAus(wert.name)}</span>
            <span className="kw-text">
              <b>{wert.name}</b>
              <span>
                {wert.ort || t("allgemein.strich")}
                {wert.nr ? ` ${t("allgemein.trennpunkt")} ${wert.nr}` : ""}
              </span>
            </span>
            {wert.art === "interessent" ? <Marke art="acht">{t("kundenwahl.interessent")}</Marke> : null}
            {wert.gesperrt ? <Marke art="stopp">{t("kundenwahl.gesperrt")}</Marke> : null}
            <span className="kw-aendern">{t("allgemein.aendern")}</span>
          </>
        ) : (
          <>
            <span className="kw-platz">{t(alle ?? "kundenwahl.waehlen")}</span>
            <span className="kw-pfeil" aria-hidden="true">{t("kundenwahl.pfeil")}</span>
          </>
        )}
      </button>
      )}
      {alle ? null : sperrsatz}

      {/* **Am Telefon das Blatt von unten, am Schreibtisch die Liste unter
          dem Anker.** `form="unten"`, obwohl ein Suchfeld darin steht:
          derselbe Grund wie beim Nachschlagen — das Feld ist die Abkürzung
          für den, der den Namen tippen will, der Regelweg ist die Liste, und
          der Streifen Maske über dem Blatt sagt, wohin der Kunde geht. */}
      {offen && telefon ? (
        <Blattvonunten
          offen
          form="unten"
          schliessen={zu}
          titel={t("kundenwahl.blattTitel")}
          {...(fussknopf ? { fuss: fussknopf } : {})}
        >
          {suchfeld}
          {liste}
        </Blattvonunten>
      ) : offen ? (
        <div className="kw-blatt">
          {suchfeld}
          {liste}
          {fussknopf}
        </div>
      ) : null}

      {mitAnlegen ? (
        <KundeAnlegen
          offen={anlegen}
          art={art}
          {...(suchwort ? { firma: suchwort } : {})}
          schliessen={() => setzeAnlegen(false)}
          angelegt={(k) => { setzeAnlegen(false); aendern(k); }}
        />
      ) : null}
    </div>
  );
}
